import React, { FC } from 'react';
import chevronRight from '../../../../shared/vector-images/base-shapes/chevronRight.js';
import type { WeatherDirectionArrowVariant } from '../../../../shared/charts/types';
import {
  renderWeatherDirectionArrowInnerHtml,
  WEATHER_DIRECTION_ARROW_SIZES,
} from '../../../../shared/charts/weatherDirectionArrows';

export type WeatherDirectionChevronDirection = 'up' | 'down' | 'left' | 'right';

const DIRECTION_ROTATION: Record<WeatherDirectionChevronDirection, number> = {
  right: 0,
  up: -90,
  down: 90,
  left: 180,
};

const CHEVRON_BOX = chevronRight.box as [number, number, number, number];
const CHEVRON_VIEW_WIDTH = CHEVRON_BOX[2] - CHEVRON_BOX[0];
const CHEVRON_VIEW_HEIGHT = CHEVRON_BOX[3] - CHEVRON_BOX[1];

/** Default rendered size for rise/set navigation chevrons (matches fontSize 1 labels). */
export const WEATHER_NAVIGATION_CHEVRON_SIZE = {
  width: 14,
  height: 22,
} as const;

export interface WeatherDirectionChevronProps {
  direction: WeatherDirectionChevronDirection;
  color: string;
  /** Block arrow used on wind/swell charts; navigation chevron for rise/set labels. */
  shape?: 'direction' | 'navigation';
  variant?: WeatherDirectionArrowVariant;
  width?: number;
  height?: number;
}

/** Direction or navigation chevron for weather UI (DOM/SVG). */
const WeatherDirectionChevron: FC<WeatherDirectionChevronProps> = ({
  direction,
  color,
  shape = 'direction',
  variant = 'thin',
  width,
  height,
}) => {
  if (shape === 'navigation') {
    const svgWidth = width ?? WEATHER_NAVIGATION_CHEVRON_SIZE.width;
    const svgHeight = height ?? WEATHER_NAVIGATION_CHEVRON_SIZE.height;
    const rotation = DIRECTION_ROTATION[direction];
    const centerX = CHEVRON_VIEW_WIDTH / 2;
    const centerY = CHEVRON_VIEW_HEIGHT / 2;

    return (
      <svg
        width={svgWidth}
        height={svgHeight}
        viewBox={CHEVRON_BOX.join(' ')}
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
      >
        <g
          transform={`rotate(${rotation}, ${centerX}, ${centerY})`}
          /* eslint-disable-next-line react/no-danger */
          dangerouslySetInnerHTML={{
            __html: chevronRight.content({ fillColor: color }),
          }}
        />
      </svg>
    );
  }

  const defaultSize = WEATHER_DIRECTION_ARROW_SIZES[variant];
  const svgWidth = width ?? defaultSize.width;
  const svgHeight = height ?? defaultSize.height;
  const rotation = DIRECTION_ROTATION[direction];

  return (
    <svg
      width={svgWidth}
      height={svgHeight}
      viewBox={`0 0 ${defaultSize.width} ${defaultSize.height}`}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <g
        transform={`rotate(${rotation}, ${defaultSize.width / 2}, ${defaultSize.height / 2})`}
        /* eslint-disable-next-line react/no-danger */
        dangerouslySetInnerHTML={{
          __html: renderWeatherDirectionArrowInnerHtml(variant, color),
        }}
      />
    </svg>
  );
};

export default WeatherDirectionChevron;
